import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { updateDriverDocumentStatus, updateDriverVehicleSafety, formatPhone } from '../services/firebase';
import ImageViewerModal from './ImageViewerModal';
import KycReviewCard from './KycReviewCard';
import { useLang } from '../lang/LangContext';

export default function DriverDocsModal({ driver, onClose, onRefresh }) {
  const { t } = useLang();
  const [busyDoc, setBusyDoc] = useState(null);
  const [rejectingDoc, setRejectingDoc] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [viewerImage, setViewerImage] = useState(null);
  const [safetyBusy, setSafetyBusy] = useState(false);
  const [safetyForm, setSafetyForm] = useState(() => ({
    published: driver?.vehicleSafety?.published === true,
    rc: { status: driver?.vehicleSafety?.rc?.status || 'unavailable', validUpto: driver?.vehicleSafety?.rc?.validUpto || driver?.documents?.rc?.expiryDate || '' },
    insurance: { status: driver?.vehicleSafety?.insurance?.status || 'unavailable', validUpto: driver?.vehicleSafety?.insurance?.validUpto || driver?.documents?.insurance?.expiryDate || '' },
    drivingLicence: { status: driver?.vehicleSafety?.drivingLicence?.status || 'unavailable', validUpto: driver?.vehicleSafety?.drivingLicence?.validUpto || driver?.documents?.drivingLicence?.expiryDate || '' },
  }));

  useEffect(() => {
    if (!driver) return;
    const current = driver.vehicleSafety || {};
    const documents = driver.documents || {};
    setSafetyForm({
      published: current.published === true,
      rc: { status: current.rc?.status || 'unavailable', validUpto: current.rc?.validUpto || documents.rc?.expiryDate || '' },
      insurance: { status: current.insurance?.status || 'unavailable', validUpto: current.insurance?.validUpto || documents.insurance?.expiryDate || '' },
      drivingLicence: { status: current.drivingLicence?.status || 'unavailable', validUpto: current.drivingLicence?.validUpto || documents.drivingLicence?.expiryDate || '' },
    });
  }, [driver?.id, driver?.vehicleSafety, driver?.documents]);

  if (!driver) return null;

  const docs = driver.documents || {};
  const dl = docs.drivingLicence || {};
  const rc = docs.rc || {};
  const ins = docs.insurance || {};
  const aadhaar = docs.aadhaar || {};

  const handleApprove = async (docKey) => {
    setBusyDoc(docKey);
    try {
      await updateDriverDocumentStatus(driver.id, docKey, 'verified');
      toast.success(`${docKey.toUpperCase()} ${t('verified successfully!')}`);
      onRefresh?.();
    } catch (err) {
      console.error('Approve doc failed:', err);
      toast.error(t('Failed to verify document'));
    } finally {
      setBusyDoc(null);
    }
  };

  const handleReject = async (docKey) => {
    if (!rejectReason.trim()) {
      toast.error(t('Please enter a rejection reason for the driver'));
      return;
    }
    setBusyDoc(docKey);
    try {
      await updateDriverDocumentStatus(driver.id, docKey, 'rejected', rejectReason.trim());
      toast.success(`${docKey.toUpperCase()} ${t('rejected. Driver notified.')}`);
      setRejectingDoc(null);
      setRejectReason('');
      onRefresh?.();
    } catch (err) {
      console.error('Reject doc failed:', err);
      toast.error(t('Failed to reject document'));
    } finally {
      setBusyDoc(null);
    }
  };

  const saveSafety = async () => {
    setSafetyBusy(true);
    try {
      await updateDriverVehicleSafety(driver.id, safetyForm);
      toast.success(t('Vehicle safety details saved'));
      onRefresh?.();
    } catch (err) {
      console.error('Save vehicle safety failed:', err);
      toast.error(t('Failed to save vehicle safety details'));
    } finally {
      setSafetyBusy(false);
    }
  };

  const updateSafetyItem = (key, field, value) => setSafetyForm((current) => ({
    ...current,
    [key]: { ...current[key], [field]: value },
  }));

  const renderBadge = (status) => {
    if (status === 'verified') return <span className="badge green">{t('✓ Verified')}</span>;
    if (status === 'pending') return <span className="badge yellow">{t('⏳ In Review')}</span>;
    if (status === 'rejected') return <span className="badge red">{t('✕ Rejected')}</span>;
    return <span className="badge gray">{t('Not Uploaded')}</span>;
  };

  // The back of a card (RC, Aadhaar): same tile as the front, with its own viewer.
  const renderBackPhoto = (url, label, subtitle) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span className="muted" style={{ fontSize: 11, fontWeight: 700 }}>{label}</span>
        {url && <span style={{ fontSize: 10, color: '#38bdf8', fontWeight: 600 }}>🔍 Click to view</span>}
      </div>
      {url ? (
        <div
          style={{ position: 'relative', cursor: 'pointer', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)', background: '#0f172a' }}
          onClick={() => setViewerImage({ url, title: `${driver.name || 'Anna'} — ${label}`, subtitle })}
          title={`Click to inspect ${label}`}
        >
          <img src={url} alt={label} style={{ width: '100%', height: 160, objectFit: 'contain', display: 'block' }} />
          <div
            style={{
              position: 'absolute', bottom: 6, right: 6,
              background: 'rgba(0,0,0,0.75)', color: '#f8fafc',
              padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
              backdropFilter: 'blur(4px)', pointerEvents: 'none',
            }}
          >
            🔍 Inspect
          </div>
        </div>
      ) : (
        <div style={{ height: 80, background: '#0f172a', borderRadius: 10, display: 'grid', placeItems: 'center', color: '#f87171', fontSize: 12 }}>No back photo</div>
      )}
    </div>
  );

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="modal-sheet"
        style={{ maxWidth: 840, maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: '#1e293b', display: 'grid', placeItems: 'center', fontSize: 24 }}>
              📄
            </div>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: '#f8fafc' }}>
                {t('KYC & Transport Documents')}: {driver.name || 'Anna'}
              </h2>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                📞 {formatPhone(driver.phone)} · Auto: <strong className="mono">{driver.vehicleNumber || '—'}</strong>
              </div>
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        {/* Specs Pill Summary */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16, background: '#0f172a', padding: 12, borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            ⛽ {t('Fuel')}: <strong style={{ color: '#38bdf8', textTransform: 'uppercase' }}>{docs.fuelType || 'Auto LPG'}</strong>
          </span>
          <span style={{ color: '#475569' }}>•</span>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            🩸 {t('Blood Group')}: <strong style={{ color: '#f87171' }}>{docs.bloodGroup || '—'}</strong>
          </span>
          <span style={{ color: '#475569' }}>•</span>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            🚨 {t('Emergency Nominee')}: <strong style={{ color: '#34d399' }}>{docs.emergencyContact ? formatPhone(docs.emergencyContact) : '—'}</strong>
          </span>
        </div>

        {/* Sign-up check: submitted?, what is missing, what the AI read, where photos came from */}
        <KycReviewCard driver={driver} />

        {/* Passenger-facing vehicle safety registry */}
        <section style={{ background: '#0f172a', borderRadius: 16, padding: 16, marginBottom: 16, border: '1px solid rgba(16,185,129,0.28)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>🛡️ {t('Passenger Vehicle Safety')}</h3>
              <p className="muted" style={{ fontSize: 11, margin: '4px 0 0' }}>{t('Publish only after checking the original records. All 3 safety records are required.')}</p>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={safetyForm.published} onChange={(e) => setSafetyForm((current) => ({ ...current, published: e.target.checked }))} />
              {t('Show to passengers')}
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginTop: 12 }}>
            {[
              ['rc', 'RC'],
              ['insurance', 'Insurance'],
              ['drivingLicence', 'Driving Licence (DL)'],
            ].map(([key, label]) => (
              <div key={key} style={{ background: '#1e293b', borderRadius: 10, padding: 10 }}>
                <strong style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>{label}</strong>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <select value={safetyForm[key].status} onChange={(e) => updateSafetyItem(key, 'status', e.target.value)} aria-label={`${label} status`}>
                    <option value="valid">{t('Valid')}</option>
                    <option value="pending">{t('Pending')}</option>
                    <option value="expired">{t('Expired')}</option>
                    <option value="unavailable">{t('Unavailable')}</option>
                  </select>
                  <input type="date" value={safetyForm[key].validUpto} onChange={(e) => updateSafetyItem(key, 'validUpto', e.target.value)} aria-label={`${label} valid upto`} />
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 12 }} disabled={safetyBusy} onClick={saveSafety}>
            {safetyBusy ? 'Saving…' : t('Save Safety Details')}
          </button>
        </section>

        {/* 1. DRIVING LICENCE */}
        <div style={{ background: '#1e293b', borderRadius: 16, padding: 16, marginBottom: 16, border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>🪪</span>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>{t('Driving Licence (DL)')}</h3>
                <span className="muted" style={{ fontSize: 11 }}>DL #: <strong className="mono">{dl.number || 'Not provided'}</strong> · Expiry: {dl.expiryDate || '—'}</span>
              </div>
            </div>
            {renderBadge(dl.status)}
          </div>

          {dl.rejectionReason && (
            <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', color: '#f87171', padding: '6px 10px', borderRadius: 8, fontSize: 12, marginBottom: 10 }}>
              ⚠️ Rejection Note: {dl.rejectionReason}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span className="muted" style={{ fontSize: 11, fontWeight: 700 }}>DL FRONT</span>
                {dl.frontPhotoUrl && (
                  <span style={{ fontSize: 10, color: '#38bdf8', fontWeight: 600 }}>🔍 Click to view</span>
                )}
              </div>
              {dl.frontPhotoUrl ? (
                <div
                  style={{ position: 'relative', cursor: 'pointer', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)' }}
                  onClick={() => setViewerImage({
                    url: dl.frontPhotoUrl,
                    title: `${driver.name || 'Anna'} — Driving Licence (Front)`,
                    subtitle: `DL #: ${dl.number || '—'} · Expiry: ${dl.expiryDate || '—'}`,
                  })}
                  title="Click to inspect high-resolution DL Front"
                >
                  <img
                    src={dl.frontPhotoUrl}
                    alt="DL Front"
                    style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                  />
                  <div
                    style={{
                      position: 'absolute', bottom: 6, right: 6,
                      background: 'rgba(0,0,0,0.75)', color: '#f8fafc',
                      padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                      backdropFilter: 'blur(4px)', pointerEvents: 'none'
                    }}
                  >
                    🔍 Inspect
                  </div>
                </div>
              ) : (
                <div style={{ height: 140, background: '#0f172a', borderRadius: 10, display: 'grid', placeItems: 'center', color: '#64748b', fontSize: 12 }}>No Front Photo</div>
              )}
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span className="muted" style={{ fontSize: 11, fontWeight: 700 }}>DL BACK</span>
                {dl.backPhotoUrl && (
                  <span style={{ fontSize: 10, color: '#38bdf8', fontWeight: 600 }}>🔍 Click to view</span>
                )}
              </div>
              {dl.backPhotoUrl ? (
                <div
                  style={{ position: 'relative', cursor: 'pointer', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)' }}
                  onClick={() => setViewerImage({
                    url: dl.backPhotoUrl,
                    title: `${driver.name || 'Anna'} — Driving Licence (Back)`,
                    subtitle: `DL #: ${dl.number || '—'} · Expiry: ${dl.expiryDate || '—'}`,
                  })}
                  title="Click to inspect high-resolution DL Back"
                >
                  <img
                    src={dl.backPhotoUrl}
                    alt="DL Back"
                    style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                  />
                  <div
                    style={{
                      position: 'absolute', bottom: 6, right: 6,
                      background: 'rgba(0,0,0,0.75)', color: '#f8fafc',
                      padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                      backdropFilter: 'blur(4px)', pointerEvents: 'none'
                    }}
                  >
                    🔍 Inspect
                  </div>
                </div>
              ) : (
                <div style={{ height: 140, background: '#0f172a', borderRadius: 10, display: 'grid', placeItems: 'center', color: '#64748b', fontSize: 12 }}>No Back Photo</div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {rejectingDoc === 'drivingLicence' ? (
              <div style={{ display: 'flex', gap: 6, width: '100%' }}>
                <input
                  type="text"
                  placeholder="Reason for DL rejection (e.g. photo blurry)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  style={{ flex: 1, background: '#0f172a', border: '1px solid #ef4444', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 13 }}
                />
                <button type="button" className="btn btn-danger btn-sm" disabled={busyDoc === 'drivingLicence'} onClick={() => handleReject('drivingLicence')}>{t('Confirm Reject')}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setRejectingDoc(null); setRejectReason(''); }}>{t('Cancel')}</button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ color: '#f87171', borderColor: 'rgba(239, 68, 68, 0.4)' }}
                  onClick={() => { setRejectingDoc('drivingLicence'); setRejectReason(''); }}
                >
                  {t('✕ Reject DL')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ background: '#059669', borderColor: '#059669' }}
                  disabled={busyDoc === 'drivingLicence' || dl.status === 'verified'}
                  onClick={() => handleApprove('drivingLicence')}
                >
                  {dl.status === 'verified' ? t('✓ DL Verified') : t('✓ Approve DL')}
                </button>
              </>
            )}
          </div>
        </div>


        {/* 2. VEHICLE RC */}
        <div style={{ background: '#1e293b', borderRadius: 16, padding: 16, marginBottom: 16, border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>🛺</span>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>{t('Vehicle Registration (RC)')}</h3>
                <span className="muted" style={{ fontSize: 11 }}>Plate: <strong className="mono">{rc.number || driver.vehicleNumber || '—'}</strong> · Owner: {rc.ownerName || '—'}</span>
              </div>
            </div>
            {renderBadge(rc.status)}
          </div>

          {rc.rejectionReason && (
            <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', color: '#f87171', padding: '6px 10px', borderRadius: 8, fontSize: 12, marginBottom: 10 }}>
              ⚠️ Rejection Note: {rc.rejectionReason}
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span className="muted" style={{ fontSize: 11, fontWeight: 700 }}>RC CARD PHOTO</span>
              {rc.photoUrl && (
                <span style={{ fontSize: 10, color: '#38bdf8', fontWeight: 600 }}>🔍 Click to view</span>
              )}
            </div>
            {rc.photoUrl ? (
              <div
                style={{ position: 'relative', cursor: 'pointer', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)', background: '#0f172a' }}
                onClick={() => setViewerImage({
                  url: rc.photoUrl,
                  title: `${driver.name || 'Anna'} — Vehicle Registration Certificate (RC)`,
                  subtitle: `Plate: ${rc.number || driver.vehicleNumber || '—'} · Owner: ${rc.ownerName || '—'}`,
                })}
                title="Click to inspect high-resolution RC Card"
              >
                <img
                  src={rc.photoUrl}
                  alt="RC Card"
                  style={{ width: '100%', height: 160, objectFit: 'contain', display: 'block' }}
                />
                <div
                  style={{
                    position: 'absolute', bottom: 6, right: 6,
                    background: 'rgba(0,0,0,0.75)', color: '#f8fafc',
                    padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                    backdropFilter: 'blur(4px)', pointerEvents: 'none'
                  }}
                >
                  🔍 Inspect
                </div>
              </div>
            ) : (
              <div style={{ height: 120, background: '#0f172a', borderRadius: 10, display: 'grid', placeItems: 'center', color: '#64748b', fontSize: 12 }}>No RC Photo</div>
            )}
          </div>
          {renderBackPhoto(rc.backPhotoUrl, 'RC CARD BACK', `Plate: ${rc.number || driver.vehicleNumber || '—'} · Owner: ${rc.ownerName || '—'}`)}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {rejectingDoc === 'rc' ? (
              <div style={{ display: 'flex', gap: 6, width: '100%' }}>
                <input
                  type="text"
                  placeholder="Reason for RC rejection"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  style={{ flex: 1, background: '#0f172a', border: '1px solid #ef4444', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 13 }}
                />
                <button type="button" className="btn btn-danger btn-sm" disabled={busyDoc === 'rc'} onClick={() => handleReject('rc')}>{t('Confirm Reject')}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setRejectingDoc(null); setRejectReason(''); }}>{t('Cancel')}</button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ color: '#f87171', borderColor: 'rgba(239, 68, 68, 0.4)' }}
                  onClick={() => { setRejectingDoc('rc'); setRejectReason(''); }}
                >
                  {t('✕ Reject RC')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ background: '#059669', borderColor: '#059669' }}
                  disabled={busyDoc === 'rc' || rc.status === 'verified'}
                  onClick={() => handleApprove('rc')}
                >
                  {rc.status === 'verified' ? t('✓ RC Verified') : t('✓ Approve RC')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* 3. AUTO INSURANCE */}
        {/* 3. AUTO INSURANCE */}
        <div style={{ background: '#1e293b', borderRadius: 16, padding: 16, border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>📜</span>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>{t('Auto Insurance Policy')}</h3>
                <span className="muted" style={{ fontSize: 11 }}>Policy #: <strong className="mono">{ins.policyNumber || '—'}</strong> · Expiry: {ins.expiryDate || '—'}</span>
              </div>
            </div>
            {renderBadge(ins.status)}
          </div>

          {ins.rejectionReason && (
            <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', color: '#f87171', padding: '6px 10px', borderRadius: 8, fontSize: 12, marginBottom: 10 }}>
              ⚠️ Rejection Note: {ins.rejectionReason}
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span className="muted" style={{ fontSize: 11, fontWeight: 700 }}>POLICY COPY PHOTO</span>
              {ins.photoUrl && (
                <span style={{ fontSize: 10, color: '#38bdf8', fontWeight: 600 }}>🔍 Click to view</span>
              )}
            </div>
            {ins.photoUrl ? (
              <div
                style={{ position: 'relative', cursor: 'pointer', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)', background: '#0f172a' }}
                onClick={() => setViewerImage({
                  url: ins.photoUrl,
                  title: `${driver.name || 'Anna'} — Auto Insurance Policy`,
                  subtitle: `Policy #: ${ins.policyNumber || '—'} · Expiry: ${ins.expiryDate || '—'}`,
                })}
                title="Click to inspect high-resolution Insurance Policy"
              >
                <img
                  src={ins.photoUrl}
                  alt="Insurance Policy"
                  style={{ width: '100%', height: 160, objectFit: 'contain', display: 'block' }}
                />
                <div
                  style={{
                    position: 'absolute', bottom: 6, right: 6,
                    background: 'rgba(0,0,0,0.75)', color: '#f8fafc',
                    padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                    backdropFilter: 'blur(4px)', pointerEvents: 'none'
                  }}
                >
                  🔍 Inspect
                </div>
              </div>
            ) : (
              <div style={{ height: 120, background: '#0f172a', borderRadius: 10, display: 'grid', placeItems: 'center', color: '#64748b', fontSize: 12 }}>No Insurance Photo</div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {rejectingDoc === 'insurance' ? (
              <div style={{ display: 'flex', gap: 6, width: '100%' }}>
                <input
                  type="text"
                  placeholder="Reason for Insurance rejection"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  style={{ flex: 1, background: '#0f172a', border: '1px solid #ef4444', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 13 }}
                />
                <button type="button" className="btn btn-danger btn-sm" disabled={busyDoc === 'insurance'} onClick={() => handleReject('insurance')}>{t('Confirm Reject')}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setRejectingDoc(null); setRejectReason(''); }}>{t('Cancel')}</button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ color: '#f87171', borderColor: 'rgba(239, 68, 68, 0.4)' }}
                  onClick={() => { setRejectingDoc('insurance'); setRejectReason(''); }}
                >
                  {t('✕ Reject Insurance')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ background: '#059669', borderColor: '#059669' }}
                  disabled={busyDoc === 'insurance' || ins.status === 'verified'}
                  onClick={() => handleApprove('insurance')}
                >
                  {ins.status === 'verified' ? t('✓ Insurance Verified') : t('✓ Approve Insurance')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* 4. AADHAAR CARD (identity, required) */}
        <div style={{ background: '#1e293b', borderRadius: 16, padding: 16, marginTop: 16, border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>🆔</span>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>{t('Aadhaar Card')}</h3>
                <span className="muted" style={{ fontSize: 11 }}>
                  Aadhaar: <strong className="mono">{aadhaar.last4 ? `XXXX XXXX ${aadhaar.last4}` : '—'}</strong>
                  {' '}· verify the full number from the photo
                </span>
              </div>
            </div>
            {renderBadge(aadhaar.status)}
          </div>

          {aadhaar.rejectionReason && (
            <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', color: '#f87171', padding: '6px 10px', borderRadius: 8, fontSize: 12, marginBottom: 10 }}>
              ⚠️ Rejection Note: {aadhaar.rejectionReason}
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span className="muted" style={{ fontSize: 11, fontWeight: 700 }}>AADHAAR CARD PHOTO</span>
              {aadhaar.photoUrl && (
                <span style={{ fontSize: 10, color: '#38bdf8', fontWeight: 600 }}>🔍 Click to view</span>
              )}
            </div>
            {aadhaar.photoUrl ? (
              <div
                style={{ position: 'relative', cursor: 'pointer', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)', background: '#0f172a' }}
                onClick={() => setViewerImage({
                  url: aadhaar.photoUrl,
                  title: `${driver.name || 'Anna'} — Aadhaar Card`,
                  subtitle: `Aadhaar ending ${aadhaar.last4 || '—'}`,
                })}
                title="Click to inspect high-resolution Aadhaar Card"
              >
                <img
                  src={aadhaar.photoUrl}
                  alt="Aadhaar Card"
                  style={{ width: '100%', height: 160, objectFit: 'contain', display: 'block' }}
                />
                <div
                  style={{
                    position: 'absolute', bottom: 6, right: 6,
                    background: 'rgba(0,0,0,0.75)', color: '#f8fafc',
                    padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                    backdropFilter: 'blur(4px)', pointerEvents: 'none'
                  }}
                >
                  🔍 Inspect
                </div>
              </div>
            ) : (
              <div style={{ height: 120, background: '#0f172a', borderRadius: 10, display: 'grid', placeItems: 'center', color: '#64748b', fontSize: 12 }}>No Aadhaar Photo</div>
            )}
          </div>
          {renderBackPhoto(aadhaar.backPhotoUrl, 'AADHAAR CARD BACK', `Aadhaar ending ${aadhaar.last4 || '—'}`)}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {rejectingDoc === 'aadhaar' ? (
              <div style={{ display: 'flex', gap: 6, width: '100%' }}>
                <input
                  type="text"
                  placeholder="Reason for Aadhaar rejection"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  style={{ flex: 1, background: '#0f172a', border: '1px solid #ef4444', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 13 }}
                />
                <button type="button" className="btn btn-danger btn-sm" disabled={busyDoc === 'aadhaar'} onClick={() => handleReject('aadhaar')}>{t('Confirm Reject')}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setRejectingDoc(null); setRejectReason(''); }}>{t('Cancel')}</button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ color: '#f87171', borderColor: 'rgba(239, 68, 68, 0.4)' }}
                  onClick={() => { setRejectingDoc('aadhaar'); setRejectReason(''); }}
                >
                  {t('✕ Reject Aadhaar')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ background: '#059669', borderColor: '#059669' }}
                  disabled={busyDoc === 'aadhaar' || aadhaar.status === 'verified'}
                  onClick={() => handleApprove('aadhaar')}
                >
                  {aadhaar.status === 'verified' ? t('✓ Aadhaar Verified') : t('✓ Approve Aadhaar')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* High-Resolution Document Viewer */}
        {viewerImage && (
          <ImageViewerModal
            imageUrl={viewerImage.url}
            title={viewerImage.title}
            subtitle={viewerImage.subtitle}
            onClose={() => setViewerImage(null)}
          />
        )}
      </div>
    </div>
  );
}
